import { diffWords } from "diff";

export type WordStatus = "added" | "removed" | "unchanged";

/** Escape HTML entities to prevent XSS in user/AI content rendered via dangerouslySetInnerHTML. */
export function escapeHtmlContent(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Regex matching markdown list markers (-, *, +, or numbered) with leading whitespace */
const LIST_MARKER_RE = /^(\s*(?:[-*+]|\d+\.)\s+)/;

/** Matches whitespace-only strings */
const WHITESPACE_ONLY_RE = /^\s*$/;

/**
 * After escaping, restore safe inline HTML tags that rich text editors
 * produce (e.g. TipTap uses <u> for underline since markdown has no
 * underline syntax). Only restores simple tags without attributes.
 */
const SAFE_HTML_TAG_RE =
  /&lt;(\/?(?:u|em|strong|s|sub|sup|mark))&gt;/g;

/**
 * Convert TipTap's `++text++` underline syntax to `<u>text</u>` HTML
 * since react-markdown doesn't support `++` natively.
 */
const PLUS_UNDERLINE_RE = /\+\+(.+?)\+\+/g;

/** Convert TipTap's ++text++ underline syntax to <u>text</u> for react-markdown rendering. */
function convertUnderlineMarkers(text: string): string {
  return text.replace(PLUS_UNDERLINE_RE, "<u>$1</u>");
}

/** Convert underline markers and newlines to markdown hard breaks for react-markdown rendering. */
function prepareForMarkdown(text: string): string {
  return convertUnderlineMarkers(text).replace(/\n/g, "  \n");
}

/**
 * Per-word normalization for content comparison.
 * Strips md formatting, HTML tags, leading/trailing punctuation, lowercases.
 */
function normalizeWord(word: string): string {
  return word
    .replace(/\*{1,3}|_{1,2}|~~|\+{2}/g, "")
    .replace(/<\/?(?:u|em|strong|s|sub|sup|mark)>/g, "")
    .replace(/^[.,;:!?()]+|[.,;:!?()]+$/g, "")
    .toLowerCase();
}

/**
 * Strip structural formatting (bullet markers, list numbers, extra whitespace)
 * to extract only the content words. Used to detect whether a change is
 * format-only (same content, different structure) vs a real content change.
 */
export function normalizeForComparison(text: string): string {
  return text
    .replace(/^\s*[-*•+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .split(/\s+/)
    .map(normalizeWord)
    .filter(Boolean)
    .join(" ");
}

/**
 * Compute a content-only diff between two strings.
 * Normalizes both texts aggressively (strip bullets, formatting, punctuation, lowercase),
 * diffs the normalized texts, then returns flat per-word statuses.
 * Returns empty array when no content changes exist (subsumes hasContentChanges).
 */
export function computeContentDiff(
  before: string,
  after: string,
): WordStatus[] {
  const normBefore = normalizeForComparison(before);
  const normAfter = normalizeForComparison(after);

  const changes = diffWords(normBefore, normAfter);

  // If no actual changes, return empty (format-only)
  if (!changes.some((c) => c.added || c.removed)) return [];

  const statuses: WordStatus[] = [];
  for (const change of changes) {
    const words = change.value.split(/\s+/).filter(Boolean);
    const status: WordStatus = change.added
      ? "added"
      : change.removed
        ? "removed"
        : "unchanged";
    for (const _ of words) {
      statuses.push(status);
    }
  }
  return statuses;
}

/**
 * Extract content words from a line (after stripping list marker).
 * Returns [marker, tokens] where marker is the list prefix (or "")
 * and tokens are whitespace-split segments (alternating content and spaces).
 */
function splitLineTokens(line: string): [string, string[]] {
  const markerMatch = line.match(LIST_MARKER_RE);
  const marker = markerMatch ? markerMatch[1] : "";
  const content = marker ? line.slice(marker.length) : line;
  return [marker, content.split(/(\s+)/)];
}

/**
 * Build highlighted markdown for one side of the diff view.
 * Walks original text line by line, consuming word statuses via a pointer.
 * Only words that genuinely changed get wrapped in <ins>/<del>.
 */
function buildHighlightedMarkdown(
  originalText: string,
  wordStatuses: WordStatus[],
  side: "after" | "before",
  plainText: boolean,
): string {
  const tag = side === "after" ? "ins" : "del";
  const myStatus: WordStatus = side === "after" ? "added" : "removed";
  const skipStatus: WordStatus =
    side === "after" ? "removed" : "added";

  let ptr = 0;

  function nextStatus(): WordStatus {
    // Skip statuses for the other panel
    while (
      ptr < wordStatuses.length &&
      wordStatuses[ptr] === skipStatus
    ) {
      ptr++;
    }
    if (ptr >= wordStatuses.length) return "unchanged";
    return wordStatuses[ptr++];
  }

  const lines = (originalText || "").split("\n");
  const outputLines: string[] = [];

  for (const line of lines) {
    const [marker, tokens] = splitLineTokens(line);

    let lineOut = marker;
    let highlightBuf = "";

    for (const token of tokens) {
      if (WHITESPACE_ONLY_RE.test(token)) {
        if (highlightBuf) {
          highlightBuf += token;
        } else {
          lineOut += token;
        }
        continue;
      }

      const norm = normalizeWord(token);
      if (!norm) {
        // Structural token (pure formatting/punctuation) — output as-is
        if (highlightBuf) {
          highlightBuf += token;
        } else {
          lineOut += plainText ? escapeHtmlContent(token) : token;
        }
        continue;
      }

      const status = nextStatus();

      if (status === myStatus) {
        highlightBuf += token;
      } else {
        if (highlightBuf) {
          lineOut += wrapTag(highlightBuf, tag, plainText);
          highlightBuf = "";
        }
        lineOut += plainText ? escapeHtmlContent(token) : token;
      }
    }

    if (highlightBuf) {
      lineOut += wrapTag(highlightBuf, tag, plainText);
    }

    outputLines.push(lineOut);
  }

  let result = outputLines.join("\n");
  if (!plainText) {
    result = prepareForMarkdown(result);
  }
  return result;
}

/** Wrap text in <ins> or <del>, escaping HTML content for safety. */
function wrapTag(
  value: string,
  tag: "ins" | "del",
  plainText: boolean,
): string {
  const escaped = escapeHtmlContent(value);
  if (plainText) return `<${tag}>${escaped}</${tag}>`;
  const withTags = escaped.replace(SAFE_HTML_TAG_RE, "<$1>");
  return `<${tag}>${withTags}</${tag}>`;
}

/** Build the "after" (new content) panel markup, wrapping additions in <ins> tags. */
export function buildAfterMarkdown(
  originalText: string,
  wordStatuses: WordStatus[],
  plainText = false,
): string {
  return buildHighlightedMarkdown(
    originalText,
    wordStatuses,
    "after",
    plainText,
  );
}

/** Build the "before" (original content) panel markup, wrapping deletions in <del> tags. */
export function buildBeforeMarkdown(
  originalText: string,
  wordStatuses: WordStatus[],
  plainText = false,
): string {
  return buildHighlightedMarkdown(
    originalText,
    wordStatuses,
    "before",
    plainText,
  );
}
